import React from 'react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../lib/language'

export default function AccessibilityStatement() {
  const { t, language } = useLanguage()

  const styles = `
    /* ADA: visible keyboard focus */
    :focus-visible {
      outline: 2px solid #C9A84C;
      outline-offset: 3px;
      border-radius: 4px;
    }

    /* ADA: skip link */
    .lca-skip {
      position: absolute; top: -100px; left: 16px;
      background: #C9A84C; color: #0D1B3E;
      padding: 8px 16px; border-radius: 8px;
      font-size: 13px; font-weight: 600;
      text-decoration: none; z-index: 9999;
      transition: top 0.2s;
    }
    .lca-skip:focus { top: 16px; }

    /* NAV */
    .lca-nav {
      padding: 14px 32px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid rgba(201,168,76,0.2);
      position: sticky;
      top: 0;
      background: #0D1B3E;
      z-index: 100;
      min-height: 64px;
    }
    .lca-brand {
      display: flex;
      align-items: center;
      text-decoration: none;
      flex-shrink: 0;
    }
    .lca-brand-name {
      font-family: 'Cormorant Garamond', serif;
      font-size: 19px;
      color: #fff;
      font-weight: 700;
      line-height: 1.2;
      white-space: nowrap;
    }
    .lca-brand-sub {
      font-size: 9px;
      color: #C9A84C;
      letter-spacing: 0.12em;
      white-space: nowrap;
      text-transform: uppercase;
    }
    .lca-nav-right {
      display: flex;
      gap: 16px;
      align-items: center;
    }
    .lca-nav-signin {
      color: rgba(255,255,255,0.8);
      font-size: 13px;
      text-decoration: none;
      transition: color 0.2s;
    }
    .lca-nav-signin:hover { color: #C9A84C; }

    /* PAGE */
    .as-page {
      background: #0D1B3E;
      min-height: 100vh;
      color: #fff;
      padding-bottom: 80px;
    }

    /* HERO */
    .as-hero {
      text-align: center;
      padding: 56px 24px 40px;
      border-bottom: 1px solid rgba(255,255,255,0.06);
    }
    .as-hero-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: rgba(201,168,76,0.1);
      border: 1px solid rgba(201,168,76,0.25);
      border-radius: 20px;
      padding: 6px 16px;
      font-size: 12px;
      color: #C9A84C;
      font-weight: 500;
      letter-spacing: 0.05em;
      margin-bottom: 20px;
    }
    .as-hero h1 {
      font-family: 'Cormorant Garamond', serif;
      font-size: 42px;
      color: #fff;
      font-weight: 700;
      margin-bottom: 12px;
      line-height: 1.15;
    }
    .as-hero-sub {
      font-size: 15px;
      color: rgba(255,255,255,0.55);
      max-width: 560px;
      margin: 0 auto;
      line-height: 1.8;
    }
    .as-updated {
      margin-top: 16px;
      font-size: 12px;
      color: rgba(255,255,255,0.3);
    }

    /* CONTENT */
    .as-content {
      max-width: 760px;
      margin: 0 auto;
      padding: 48px 24px 0;
    }

    /* SECTIONS */
    .as-section {
      margin-bottom: 40px;
    }
    .as-section h2 {
      font-family: 'Cormorant Garamond', serif;
      font-size: 24px;
      color: #fff;
      font-weight: 700;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px solid rgba(201,168,76,0.2);
    }
    .as-section p {
      font-size: 14px;
      color: rgba(255,255,255,0.65);
      line-height: 1.85;
      margin-bottom: 12px;
    }

    /* COMPLIANCE BADGE */
    .as-compliance {
      background: rgba(201,168,76,0.06);
      border: 1px solid rgba(201,168,76,0.25);
      border-radius: 16px;
      padding: 24px;
      margin-bottom: 40px;
      display: flex;
      align-items: center;
      gap: 20px;
      flex-wrap: wrap;
    }
    .as-compliance-icon {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: #C9A84C;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      flex-shrink: 0;
    }
    .as-compliance-text { flex: 1; min-width: 200px; }
    .as-compliance-title {
      font-size: 16px;
      font-weight: 600;
      color: #C9A84C;
      margin-bottom: 4px;
    }
    .as-compliance-desc {
      font-size: 13px;
      color: rgba(255,255,255,0.6);
      line-height: 1.6;
    }

    /* FEATURE LIST */
    .as-feature-list {
      list-style: none;
      padding: 0;
      margin: 0 0 12px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }
    @media(max-width: 560px) {
      .as-feature-list { grid-template-columns: 1fr; }
    }
    .as-feature-item {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      font-size: 13px;
      color: rgba(255,255,255,0.65);
      line-height: 1.6;
      padding: 10px 14px;
      background: rgba(255,255,255,0.03);
      border: 1px solid rgba(255,255,255,0.07);
      border-radius: 10px;
    }
    .as-feature-check {
      color: #C9A84C;
      font-weight: 700;
      flex-shrink: 0;
      margin-top: 1px;
    }

    /* KNOWN ISSUES */
    .as-issues-list {
      list-style: none;
      padding: 0;
      margin: 0 0 12px;
    }
    .as-issues-item {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      font-size: 13px;
      color: rgba(255,255,255,0.65);
      line-height: 1.6;
      padding: 8px 0;
      border-bottom: 1px solid rgba(255,255,255,0.05);
    }
    .as-issues-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #C9A84C;
      flex-shrink: 0;
      margin-top: 6px;
    }

    /* CONTACT CARD */
    .as-contact {
      background: rgba(255,255,255,0.03);
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 16px;
      padding: 24px;
      margin-bottom: 40px;
    }
    .as-contact h2 {
      font-family: 'Cormorant Garamond', serif;
      font-size: 22px;
      color: #fff;
      font-weight: 700;
      margin-bottom: 8px;
    }
    .as-contact p {
      font-size: 14px;
      color: rgba(255,255,255,0.6);
      line-height: 1.8;
      margin-bottom: 16px;
    }
    .as-contact-methods {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
    }
    .as-contact-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 20px;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 600;
      text-decoration: none;
      transition: opacity 0.2s, transform 0.15s;
    }
    .as-contact-btn:hover { opacity: 0.9; transform: translateY(-1px); }
    .as-contact-btn-gold {
      background: linear-gradient(135deg, #C9A84C 0%, #e2c068 50%, #C9A84C 100%);
      color: #0D1B3E;
    }
    .as-contact-btn-outline {
      background: rgba(255,255,255,0.08);
      color: #fff;
      border: 1px solid rgba(255,255,255,0.18);
    }

    /* FOOTER */
    .as-footer {
      border-top: 1px solid rgba(201,168,76,0.2);
      padding: 24px 32px;
      margin-top: 40px;
    }
    .as-footer-inner {
      max-width: 760px;
      margin: 0 auto;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
    }
    .as-footer-copy {
      font-family: 'Cormorant Garamond', serif;
      font-size: 12px;
      color: rgba(255,255,255,0.35);
    }
    .as-footer-links {
      display: flex;
      gap: 20px;
      font-size: 12px;
    }
    .as-footer-links a {
      color: rgba(255,255,255,0.35);
      text-decoration: none;
      transition: color 0.2s;
    }
    .as-footer-links a:hover { color: #C9A84C; }

    /* RESPONSIVE */
    @media (max-width: 640px) {
      .lca-nav { padding: 12px 16px; }
      .as-hero { padding: 36px 16px 28px; }
      .as-hero h1 { font-size: 28px; }
      .as-content { padding: 32px 16px 0; }
      .as-compliance { flex-direction: column; }
      .as-footer { padding: 20px 16px; }
      .as-footer-inner { flex-direction: column; text-align: center; }
      .as-footer-links { justify-content: center; }
      .as-contact-methods { flex-direction: column; }
      .as-contact-btn { justify-content: center; }
    }
  `

  const isEs = language === 'es'

  return (
    <div className="as-page">
      <style>{styles}</style>

      {/* ADA: Skip link */}
      <a href="#as-main" className="lca-skip">
        {isEs ? 'Saltar al contenido principal' : 'Skip to main content'}
      </a>

      {/* NAV */}
      <nav role="navigation" aria-label="Main navigation">
        <div className="lca-nav">
          <Link to="/" className="lca-brand" aria-label="Loan Closing Agent LLC — Go to homepage">
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span className="lca-brand-name">Loan Closing Agent LLC</span>
              <span className="lca-brand-sub">NATIONWIDE SIGNINGS &amp; NOTARY GROUP</span>
            </div>
          </Link>
          <div className="lca-nav-right">
            <Link to="/login" className="lca-nav-signin">
              {isEs ? 'Iniciar Sesión' : 'Sign In'}
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header className="as-hero">
        <div className="as-hero-badge" aria-label="WCAG 2.1 Level AA Compliant">
          <span aria-hidden="true">♿</span>
          WCAG 2.1 Level AA
        </div>
        <h1>
          {isEs ? 'Declaración de Accesibilidad' : 'Accessibility Statement'}
        </h1>
        <p className="as-hero-sub">
          {isEs
            ? 'Loan Closing Agent LLC está comprometido con garantizar la accesibilidad digital para todas las personas, incluidas aquellas con discapacidades.'
            : 'Loan Closing Agent LLC is committed to ensuring digital accessibility for all people, including those with disabilities.'}
        </p>
        <p className="as-updated">
          {isEs ? 'Última actualización: abril de 2026' : 'Last updated: April 2026'}
        </p>
      </header>

      {/* MAIN CONTENT */}
      <main id="as-main" tabIndex={-1}>
        <div className="as-content">

          {/* Compliance badge */}
          <div className="as-compliance" role="region" aria-label="Compliance status">
            <div className="as-compliance-icon" aria-hidden="true">♿</div>
            <div className="as-compliance-text">
              <div className="as-compliance-title">
                {isEs ? 'Objetivo de Conformidad' : 'Conformance Target'}
              </div>
              <div className="as-compliance-desc">
                {isEs
                  ? 'Nos esforzamos por cumplir con las Pautas de Accesibilidad al Contenido Web (WCAG) 2.1, Nivel AA. Estas pautas explican cómo hacer que el contenido web sea más accesible para personas con discapacidades.'
                  : 'We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1, Level AA. These guidelines explain how to make web content more accessible to people with disabilities.'}
              </div>
            </div>
          </div>

          {/* Our commitment */}
          <section className="as-section" aria-labelledby="commitment-heading">
            <h2 id="commitment-heading">
              {isEs ? 'Nuestro Compromiso' : 'Our Commitment'}
            </h2>
            <p>
              {isEs
                ? 'Loan Closing Agent LLC cree que todos merecen acceso igual a los servicios notariales y de firma de préstamos, independientemente de sus capacidades. Nos comprometemos continuamente a mejorar la experiencia de usuario para todos nuestros clientes.'
                : 'Loan Closing Agent LLC believes everyone deserves equal access to notary and loan signing services, regardless of their abilities. We are continuously working to improve the user experience for all of our clients.'}
            </p>
            <p>
              {isEs
                ? 'Hemos tomado las siguientes medidas para garantizar la accesibilidad en toda nuestra plataforma:'
                : 'We have taken the following steps to ensure accessibility throughout our platform:'}
            </p>
          </section>

          {/* Accessibility features */}
          <section className="as-section" aria-labelledby="features-heading">
            <h2 id="features-heading">
              {isEs ? 'Características de Accesibilidad' : 'Accessibility Features'}
            </h2>
            <ul className="as-feature-list">
              {[
                isEs ? 'Navegación completa por teclado' : 'Full keyboard navigation support',
                isEs ? 'Compatibilidad con lectores de pantalla' : 'Screen reader compatibility',
                isEs ? 'Indicadores de enfoque visibles' : 'Visible focus indicators',
                isEs ? 'Texto alternativo en todas las imágenes' : 'Alternative text on all images',
                isEs ? 'Contraste de color WCAG AA' : 'WCAG AA color contrast ratios',
                isEs ? 'Estructura de encabezados semánticos' : 'Semantic heading structure',
                isEs ? 'Etiquetas ARIA en formularios' : 'ARIA labels on all form fields',
                isEs ? 'Enlace "Saltar al contenido"' : 'Skip to main content links',
                isEs ? 'Anuncios de errores accesibles' : 'Accessible error announcements',
                isEs ? 'Soporte bilingüe (inglés y español)' : 'Bilingual support (English & Spanish)',
                isEs ? 'Diseño responsivo para móviles' : 'Fully mobile-responsive design',
                isEs ? 'Sin contenido que parpadee o destelle' : 'No flashing or strobing content',
              ].map(feature => (
                <li key={feature} className="as-feature-item">
                  <span className="as-feature-check" aria-hidden="true">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
          </section>

          {/* Technical specifications */}
          <section className="as-section" aria-labelledby="tech-heading">
            <h2 id="tech-heading">
              {isEs ? 'Especificaciones Técnicas' : 'Technical Specifications'}
            </h2>
            <p>
              {isEs
                ? 'La accesibilidad de loanclosingagent.com se basa en las siguientes tecnologías:'
                : 'The accessibility of loanclosingagent.com relies on the following technologies:'}
            </p>
            <ul className="as-issues-list" aria-label={isEs ? 'Tecnologías utilizadas' : 'Technologies used'}>
              {['HTML5', 'CSS3', 'WAI-ARIA 1.2', 'React 18', isEs ? 'JavaScript moderno' : 'Modern JavaScript'].map(tech => (
                <li key={tech} className="as-issues-item">
                  <div className="as-issues-dot" aria-hidden="true" />
                  {tech}
                </li>
              ))}
            </ul>
            <p>
              {isEs
                ? 'Nuestra plataforma ha sido probada con los siguientes navegadores y tecnologías de asistencia: Chrome con NVDA, Safari con VoiceOver (macOS e iOS), y Firefox con JAWS.'
                : 'Our platform has been tested with the following browsers and assistive technologies: Chrome with NVDA, Safari with VoiceOver (macOS and iOS), and Firefox with JAWS.'}
            </p>
          </section>

          {/* Known limitations */}
          <section className="as-section" aria-labelledby="limitations-heading">
            <h2 id="limitations-heading">
              {isEs ? 'Limitaciones Conocidas' : 'Known Limitations'}
            </h2>
            <p>
              {isEs
                ? 'Aunque nos esforzamos por alcanzar el máximo nivel de accesibilidad, puede haber algunas áreas que aún estamos mejorando:'
                : 'Although we strive for the highest level of accessibility, there may be some areas we are still improving:'}
            </p>
            <ul className="as-issues-list" aria-label={isEs ? 'Limitaciones conocidas' : 'Known limitations'}>
              {[
                isEs
                  ? 'Algunos documentos PDF generados por terceros pueden no ser completamente accesibles'
                  : 'Some third-party generated PDF documents may not be fully accessible',
                isEs
                  ? 'El portal de Stripe para pagos es administrado por un tercero y puede tener sus propias limitaciones de accesibilidad'
                  : 'The Stripe payment portal is managed by a third party and may have its own accessibility limitations',
                isEs
                  ? 'Ciertos gráficos de datos complejos pueden requerir mejoras adicionales de descripción'
                  : 'Certain complex data charts may require additional description improvements',
              ].map(issue => (
                <li key={issue} className="as-issues-item">
                  <div className="as-issues-dot" aria-hidden="true" />
                  {issue}
                </li>
              ))}
            </ul>
            <p>
              {isEs
                ? 'Estamos trabajando activamente para resolver estas limitaciones. Si encuentra un problema, por favor contáctenos.'
                : 'We are actively working to resolve these limitations. If you encounter an issue, please contact us.'}
            </p>
          </section>

          {/* Feedback & contact */}
          <div className="as-contact" role="region" aria-labelledby="contact-heading">
            <h2 id="contact-heading">
              {isEs ? '¿Necesita Ayuda o Encontró un Problema?' : 'Need Help or Found an Issue?'}
            </h2>
            <p>
              {isEs
                ? 'Si experimenta barreras de accesibilidad en nuestro sitio web o necesita información en un formato alternativo, nos encantaría saber de usted. Intentamos responder a todos los comentarios de accesibilidad dentro de 2 días hábiles.'
                : 'If you experience any accessibility barriers on our website, or need information in an alternative format, we would love to hear from you. We aim to respond to all accessibility feedback within 2 business days.'}
            </p>
            <div className="as-contact-methods">
              <a
                href="mailto:nancy@loanclosingagent.com?subject=Accessibility%20Feedback"
                className="as-contact-btn as-contact-btn-gold"
                aria-label={isEs
                  ? 'Enviar comentarios de accesibilidad por correo electrónico'
                  : 'Send accessibility feedback by email'}
              >
                <span aria-hidden="true">✉️</span>
                {isEs ? 'Enviar Comentarios' : 'Send Feedback'}
              </a>
              <Link
                to="/login"
                className="as-contact-btn as-contact-btn-outline"
                aria-label={isEs ? 'Iniciar sesión en su cuenta' : 'Sign in to your account'}
              >
                <span aria-hidden="true">🔐</span>
                {isEs ? 'Iniciar Sesión' : 'Sign In'}
              </Link>
            </div>
          </div>

          {/* Legal */}
          <section className="as-section" aria-labelledby="legal-heading">
            <h2 id="legal-heading">
              {isEs ? 'Marco Legal' : 'Legal Framework'}
            </h2>
            <p>
              {isEs
                ? 'Esta declaración de accesibilidad se preparó de conformidad con la Ley de Estadounidenses con Discapacidades (ADA), Título III, y las Pautas de Accesibilidad al Contenido Web (WCAG) 2.1. Loan Closing Agent LLC está comprometido a proporcionar un acceso igual y oportunidades iguales a las personas con diversas discapacidades.'
                : 'This accessibility statement was prepared in accordance with the Americans with Disabilities Act (ADA), Title III, and the Web Content Accessibility Guidelines (WCAG) 2.1. Loan Closing Agent LLC is committed to providing equal access and equal opportunity to people with diverse abilities.'}
            </p>
            <p>
              {isEs
                ? 'Si cree que no hemos respondido adecuadamente a su solicitud de accesibilidad, tiene derecho a presentar una queja ante el Departamento de Justicia de los Estados Unidos.'
                : 'If you believe we have not responded adequately to your accessibility request, you have the right to file a complaint with the U.S. Department of Justice.'}
            </p>
          </section>

        </div>
      </main>

      {/* FOOTER */}
      <footer className="as-footer" role="contentinfo">
        <div className="as-footer-inner">
          <div className="as-footer-copy">
            © 2019–2026 Loan Closing Agent LLC · DBA Nationwide Notary Professionals Network
          </div>
          <nav className="as-footer-links" aria-label="Footer navigation">
            <Link to="/login">{isEs ? 'Iniciar Sesión' : 'Sign In'}</Link>
            <Link to="/register">{isEs ? 'Registrarse' : 'Register'}</Link>
            <a href="mailto:nancy@loanclosingagent.com">{isEs ? 'Contacto' : 'Contact'}</a>
          </nav>
        </div>
        <div style={{ textAlign:'center', marginTop:12, fontSize:10, color:'rgba(255,255,255,0.3)', fontStyle:'italic' }}>
          Loan Closing Agent, LLC is not a law firm and does not provide legal advice.
        </div>
      </footer>
    </div>
  )
}
